<template>
	<div class="date-picker-menu-container" :class="{ 'with-timezone': userSelectionTimezoneEnabled }">
		<default-widget-field v-bind="defaultFieldProps" v-on="defaultFieldListeners" />
		<AppDatePickerMenu :ref="fieldRef" v-model="formattedDate" :min="minDate" :max="maxDate" />
		<AppTimePickerMenu
			v-model="formattedTime"
			:min="minTime"
			:max="maxTime"
			:format="format"
			:allowed-hours="allowedHours"
			:allowed-minutes="allowedMinutes"
			@click:hour="chooseHour"
		/>
		<div v-if="userSelectionTimezoneEnabled" class="pt-2">
			<SelectTimezone class="mt-2" :userTimezone.sync="timezone" />
			<div class="px-2 sub-text">Suggested timezone: {{ detectedTimezone }}.</div>
		</div>
		<div v-if="showTimezone" class="pa-2 iso">Timezone: {{ localTimezone }}</div>
	</div>
</template>

<script>
import moment from 'moment';
import TimezoneMoment from 'moment-timezone';
import WidgetFieldMixin from './WidgetFieldMixin';
import DefaultWidgetField from './DefaultWidgetField';
import SelectTimezone from '@/components/SelectTimezone.vue';
import { DATE_TIME_TIMEZONE_OPTIONS } from '@/utils/widget-service.const';

export default {
	name: 'DateTimePickerField',
	components: {
		SelectTimezone,
		DefaultWidgetField,
	},
	mixins: [WidgetFieldMixin],
	computed: {
		timezonesOptions() {
			return TimezoneMoment.tz.names();
		},
		detectedTimezone() {
			return Intl.DateTimeFormat().resolvedOptions().timeZone;
		},
		date() {
			return this.localVal?.split(' ')?.[0] || this.getProcessedValue(this.value?.date) || '';
		},
		time() {
			return this.localVal?.split(' ')?.[1] || this.getProcessedValue(this.value?.time) || '';
		},
		prefillEnabled() {
			return this.fieldMetadata?.options?.prefill?.enabled;
		},
		formattedDate: {
			set(value) {
				if (value) {
					this.localVal = this.getDateAndTime(value, this.time);
				}
			},
			get() {
				return this.date;
			},
		},
		formattedTime: {
			set(value) {
				if (value) {
					this.localVal = this.getDateAndTime(this.date, value);
				}
			},
			get() {
				return this.time;
			},
		},
		format() {
			return this.fieldMetadata?.options?.time?.all_hours_format?.enabled ? '24hr' : 'ampm';
		},
		fieldValidations() {
			return this.fieldMetadata.validations;
		},
		minDateValidation() {
			return this.fieldValidations.min_date;
		},
		maxDateValidation() {
			return this.fieldValidations.max_date;
		},
		minTimeValidation() {
			return this.fieldValidations.min_time;
		},
		maxTimeValidation() {
			return this.fieldValidations.max_time;
		},
		timeInterval() {
			return this.fieldValidations.time_interval;
		},
		timeIntervalValue() {
			return this.getProcessedValue(this.timeInterval.value);
		},
		userSelectionTimezoneEnabled() {
			return this.fieldMetadata.options?.timezone?.option_selected?.value === DATE_TIME_TIMEZONE_OPTIONS.letUserChoose;
		},
		useDetectedTimezoneEnabled() {
			return this.fieldMetadata.options?.timezone?.option_selected?.value === DATE_TIME_TIMEZONE_OPTIONS.automaticallyDetect;
		},
		showTimezone() {
			return this.fieldMetadata.options?.timezone?.show?.enabled;
		},
		customTimezone() {
			return this.fieldMetadata.options?.timezone?.custom?.value || 'UTC';
		},
		localTimezone() {
			return this.userSelectionTimezoneEnabled
				? this.timezone
				: this.useDetectedTimezoneEnabled
				? this.detectedTimezone
				: this.customTimezone;
		},
		minDate() {
			return this.minDateValidation.enabled ? this.getProcessedValue(this.minDateValidation.value) : '';
		},
		maxDate() {
			return this.maxDateValidation.enabled ? this.getProcessedValue(this.maxDateValidation.value) : '';
		},
		minTime() {
			return this.minTimeValidation.enabled ? this.getProcessedValue(this.minTimeValidation.value) : '';
		},
		maxTime() {
			return this.maxTimeValidation.enabled ? this.getProcessedValue(this.maxTimeValidation.value) : '';
		},
	},
	methods: {
		getIsoDate(value) {
			if (!value) {
				return '';
			} else {
				const keepOffset = true;
				const momentValue = moment.tz(value, this.localTimezone);
				return momentValue.toISOString(keepOffset);
			}
		},
		allowedMinutes(m) {
			return this.timeInterval.enabled ? this.allowedHoursMinutesMap[this.chosenHour]?.includes(m) : true;
		},
		allowedHours(m) {
			return this.timeInterval.enabled ? Object.keys(this.allowedHoursMinutesMap).includes(String(m)) : true;
		},
		chooseHour(v) {
			this.chosenHour = String(v);
		},
		getDateAndTime(date, time) {
			return date + ' ' + time;
		},
	},
	mounted() {
		this.timezone = this.detectedTimezone;
		if (this.prefillEnabled) {
			//set processed prefill
			const processedDate = this.getProcessedValue(this.value?.date);
			const processedTime = this.getProcessedValue(this.value?.time);
			this.value = {
				iso: this.getIsoDate(this.getDateAndTime(processedDate, processedTime)),
				date: processedDate,
				time: processedTime,
				timezone: this.localTimezone,
			};
		}
		if (this.timeInterval.enabled && this.timeIntervalValue) {
			let today = moment().minutes(0).hours(0);
			let maxHour = 24;
			let maxMinute = 60;
			if (this.minTime) {
				try {
					const hourMin = this.minTime?.split(':');
					today = moment().minutes(hourMin[1]).hours(hourMin[0]);
				} catch (err) {
					console.log(err);
				}
			}
			if (this.maxTime) {
				try {
					const hourMin = this.maxTime?.split(':');
					maxHour = Number(hourMin[0]);
					maxMinute = Number(hourMin[1]);
				} catch (err) {
					console.log(err);
				}
			}

			const todayDay = moment().day();
			while ((today.hour() <= maxHour || today.minute() <= maxMinute) && today.day() <= todayDay) {
				if (!this.allowedHoursMinutesMap[today.hour()]) {
					this.allowedHoursMinutesMap[today.hour()] = [];
				}
				if (!this.allowedHoursMinutesMap[today.hour()].includes(today.minute())) {
					this.allowedHoursMinutesMap[today.hour()].push(today.minute());
				}
				today.add(this.timeIntervalValue, 'minutes');
			}
		}
	},
	data: () => ({
		menu: false,
		localVal: null,
		timezone: null,
		chosenHour: null,
		allowedHoursMinutesMap: {},
	}),
	watch: {
		localVal(newV) {
			this.value = {
				iso: this.getIsoDate(newV),
				date: this.date,
				time: this.time,
				timezone: this.localTimezone,
			};
		},
	},
};
</script>
<style scoped lang="scss">
.date-picker-menu-container {
	height: 180px;
	&.with-timezone {
		height: 250px;
	}
	.sub-text,
	.iso {
		font-size: 12px;
		opacity: 0.5;
	}
	@media (max-width: 575px) {
		min-width: 290px !important;
	}
}
</style>
